import { forwardRef, ReactElement, RefObject, useState } from 'react';
import { CardMedia, DividerProps, useMediaQuery, useTheme, Typography, IconButton } from '@mui/material';
import flowingCSImage from '@/assets/images/sources/flowing_cs_banner.png';

import { Box, Drawer, DrawerProps, List, Paper } from '@mui/material';
import ExpandableMenuItem from '@/layouts/ManagerLayout/ExpandableMenuItem';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import FlowingCsSvg from '@/assets/images/logo/flowing_space_logo_signiture.svg';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { useSpaceUserV2Store } from '@/store';
interface MenuItemBase {
    id?: string | number;
    label: string;
    icon?: ReactElement;
    to?: string;
    href?: string;
    target?: string;
    action?: () => void;
    isExistMatch?: boolean;
}

export interface MenuItem extends MenuItemBase {
    children?: MenuItemBase[] | ReactElement;
    isShowDivider?: boolean;
    DividerProps?: DividerProps;
    isActive?: boolean;
}
export interface MenuTitleListItem {
    menuTitle: string;
    menuTitleImg: any;
    menuList: MenuItem[];
}

interface Props extends DrawerProps {
    isDrawerOpen?: boolean;
    setIsDrawerOpen?: (open: boolean) => void;
    appBarHeight?: number;
    menuTitleList?: MenuTitleListItem[];
    append?: ReactElement;
}

const MenuDrawer = (props: Props, ref: any) => {
    const { isDrawerOpen = true, setIsDrawerOpen, appBarHeight = 64, menuTitleList = [], append } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { t } = useTranslation();
    const { grade_name } = useSpaceUserV2Store();
    const isSodomall = grade_name === '소도몰';
    const MenuTitleBox = ({ title, img = '', isMt }: { title: string; img: string; isMt: boolean }) => {
        return (
            <Box className={`flex pl-3 items-center gap-2`} sx={{ mt: isMt ? 3 : 2 }}>
                <CardMedia component={'img'} src={img} sx={{ width: '35px', height: '35px' }} />
                <Typography>
                    <b>{title}</b>
                </Typography>
            </Box>
        );
    };

    return (
        <Box sx={{ position: 'relative' }}>
            <Drawer
                PaperProps={{
                    ref: ref,
                    sx: {
                        minWidth: 240,
                        maxWidth: 320,
                        marginTop: `${appBarHeight}px`,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        overflowY: 'auto',
                        // 스크롤바 기본적으로 숨김
                        '&::-webkit-scrollbar': {
                            width: '0px',
                            background: 'transparent',
                        },
                        // 호버 시에만 스크롤바 표시
                        '&:hover::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#ccc',
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: '#999',
                        },
                        // Firefox용 스크롤바 스타일링
                        scrollbarWidth: 'none',
                        '&:hover': {
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#ccc transparent',
                        },
                    },
                }}
                variant={!isMobile ? 'persistent' : 'temporary'}
                hideBackdrop={true}
                open={isDrawerOpen}
                sx={{
                    flexShrink: 0,
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        paddingBottom: !props?.append ? `${appBarHeight}px` : undefined,
                    }}
                >
                    {menuTitleList.map((item, idx) => {
                        return (
                            <div key={idx}>
                                <MenuTitleBox title={item.menuTitle} isMt={idx !== 0} img={item.menuTitleImg} />
                                <List
                                    sx={{
                                        '& .MuiListItemIcon-root': { minWidth: 35 },
                                    }}
                                >
                                    {item.menuList.map((item, index) => {
                                        return <ExpandableMenuItem {...item} key={item?.id || index} />;
                                    })}
                                </List>
                            </div>
                        );
                    })}
                    {/* 고객센터 & 사용자 가이드 - 소도몰 역할일 때 숨김 */}
                    {!isSodomall && (
                        <List
                            sx={{
                                '& .MuiListItemIcon-root': { minWidth: 35 },
                                mt: 5,
                            }}
                        >
                            <ExpandableMenuItem
                                label={t('product.customerCenter')}
                                target="_blank"
                                href="http://pf.kakao.com/_BxfaRn/chat"
                                // href='https://help.flowing.space/hc/ko/requests/new'
                                isShowDivider
                                icon={<SupportAgentIcon />}
                            />
                            <ExpandableMenuItem
                                label={t('sidebar.userGuide')}
                                target="_blank"
                                href="https://flowing.oopy.io/guide/seller/mall"
                                // isShowDivider
                                icon={<ArticleOutlinedIcon />}
                            />
                        </List>
                    )}
                </Paper>
                {!!append ? (
                    <div
                        style={{
                            paddingBottom: appBarHeight,
                        }}
                    >
                        {append}
                    </div>
                ) : (
                    ''
                )}
            </Drawer>

            {/* 사이드바 토글 버튼 - 사이드바 우측 경계선에 붙임 */}
            {setIsDrawerOpen && !isMobile && (
                <IconButton
                    onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                    sx={{
                        position: 'fixed',
                        left: isDrawerOpen ? '240px' : '0px',
                        top: '50%',
                        transform: isDrawerOpen ? 'translateY(-50%) translateX(-50%)' : 'translateY(-50%)',
                        zIndex: 'calc( var(--v-zIndex-drawer) + 2)',
                        backgroundColor: isDrawerOpen ? '#fff' : 'primary.main',
                        border: isDrawerOpen ? '1px solid' : '2px solid',
                        borderColor: isDrawerOpen ? 'divider' : 'primary.dark',
                        borderLeft: 'none',
                        boxShadow: isDrawerOpen ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '2px 2px 12px rgba(0, 0, 0, 0.3)',
                        width: '28px',
                        height: '48px',
                        padding: '4px',
                        borderRadius: '0 8px 8px 0',
                        transition: 'all 225ms cubic-bezier(0, 0, 0.2, 1)',
                        '&:hover': {
                            backgroundColor: isDrawerOpen ? 'primary.light' : 'primary.dark',
                            borderColor: 'primary.dark',
                            boxShadow: isDrawerOpen ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '2px 2px 16px rgba(0, 0, 0, 0.4)',
                            transform: isDrawerOpen ? 'translateY(-50%) translateX(-50%)' : 'translateY(-50%) translateX(2px)',
                            '& .MuiSvgIcon-root': {
                                color: isDrawerOpen ? 'primary.dark' : '#fff',
                            },
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '20px',
                            color: isDrawerOpen ? 'text.secondary' : '#fff',
                            transition: 'color 0.2s',
                        },
                    }}
                    aria-label={isDrawerOpen ? '사이드바 닫기' : '사이드바 열기'}
                    title={isDrawerOpen ? '사이드바 닫기' : '사이드바 열기'}
                >
                    {isDrawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
            )}
        </Box>
    );
};

export default forwardRef(MenuDrawer);
